import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from 'angular-toastify';
import { Subject, takeUntil } from 'rxjs';
import { Action } from 'src/app/core/models/action.model';
import { DynamicService } from 'src/app/core/services/base/dynamic.service';
import { saveData } from 'src/app/core/services/fileTemplater.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { ConfirmModalComponent } from 'src/app/shared/components/simple/confirm-modal/confirm-modal.component';
import { DownloadDocumentComponent } from '../../../../simple/os-action-item/modals/download-document/download-document.component';
import { PendingSignatureComponent } from '../../../../simple/os-action-item/modals/pending-signature/pending-signature.component';
import { EletronicSignatureModalComponent } from '../../../electronic-signature/components/eletronic-signature-modal/eletronic-signature-modal.component';
import { OaSignatureService } from './service/oa-signature.service';

@Component({
  selector: 'app-oa-signature',
  templateUrl: './oa-signature.component.html',
  styleUrls: ['./oa-signature.component.scss', '../oa-styles.scss'],
})
export class OaSignatureComponent implements OnDestroy {
  @Input() action!: Action;
  @Output() changed = new EventEmitter<boolean>();

  infoPendingMessage = 'This action is pending. Please wait.';

  isLoading = false;

  destroySubject = new Subject<void>();

  constructor(
    public dialog: MatDialog,
    private ds: DynamicService<any>,
    private service: OaSignatureService,
    private ts: ToastService,
    private translate: TranslateService,
    private ss: StorageService
  ) {}

  ngOnInit() {
    // this.signatureDialog();
    // get case config

    this.ds.load('cpv_actions_complete');
    this.ds
      .autofillQuery('cp_case_actions', '*', [
        { op: 'eq', col: 'action_id', val: this.action.action_id! },
      ])
      .then((data) => {
        if (data.length > 0) {
          this.infoPendingMessage = (data[0] as any).config.info_pending;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  signatureDialog() {
    // If No signature or failed, display signing pad.
    let dialogRef;
    if (this.action.ac_status !== 'DONE') {
      dialogRef = this.dialog.open(EletronicSignatureModalComponent, {
        data: {
          contract_id: this.action.contract_id,
          claim_id: this.action.claim_id,
        },
        width: 'min(90vw, 720px)',
      });
      dialogRef.afterClosed().subscribe((result) => {
        console.log(`Dialog result: ${result}`);
        this.changed.emit(true);
      });
    } else if ((this.action.ac_status as any) === 'SIGNED') {
      // If there is a signature and it's fine, display popup with download option
      dialogRef = this.dialog.open(DownloadDocumentComponent, {
        data: {
          signed_document_id: this.action.done_action_id,
        },
      });
    } else {
      // If there is a signature, but its pending, display please wait.
      dialogRef = this.dialog.open(PendingSignatureComponent, {
        data: {
          signed_document_id: this.action.done_action_id,
        },
      });
    }
  }

  checkDependsOn() {
    return (this.action.prstatus as any) === 'DONE';
  }

  checkStatus() {
    let status = 'OPEN';

    // se o ac_status for igual a 'SIGNED', está concluido
    if ((this.action.ac_status as any) === 'SIGNED') {
      return 'DONE';
    }

    if (this.action.ac_status === 'PENDING') {
      status = 'PENDING';
    }

    // se o ac_status for diferente de 'DONE' mas o prstatus for igual a 'SUBMITTED' esta em processamento
    if (
      (this.action.ac_status as any) !== 'DONE' &&
      (this.action.prstatus as any) === 'OPEN'
    ) {
      status = 'BLOCKED';
    }

    return status;
  }

  async downloadDocument() {
    this.isLoading = true;
    try {
      const res = await this.service.getAllGroupedByCase(this.action.claim_id!);
      if (res?.url != null) {
        const url = res.url;
        const [bucket, ...pathList] = url.split('/');
        const path = pathList.join('/');
        const ext = pathList.reverse()[0].split('.').reverse()[0];
        const { data: file, error } = await this.ss.download(bucket, path);

        if (error) {
          console.error('Your document could not be downloaded', error);
          this._showErrorSnackbar('Your document could not be downloaded');
          return;
        }

        if (!file) {
          console.error('File not found');
          this._showErrorSnackbar('File not found');
          return;
        }

        saveData(file, (this.action?.action_name || 'Contract') + '.' + ext);
      } else {
        this._showErrorSnackbar('Error on download document');
      }
    } catch (err) {
      console.error(err);
      this._showErrorSnackbar('Error on download document');
    } finally {
      this.isLoading = false;
    }
  }

  retractSignature() {
    this.isLoading = true;

    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          message: 'Are you sure you want to retract your signature?',
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroySubject))
      .subscribe(async (result) => {
        try {
          if (result) {
            const res = await this.service.getAllGroupedByCase(
              this.action.claim_id!
            );
            if (res) {
              await this.service.retract(res.id);
              this.changed.emit(true);
            } else {
              throw 'without signature';
            }
          }
        } catch (err) {
          console.error(err);
          this._showErrorSnackbar('Error on retract signature');
        } finally {
          this.isLoading = false;
        }
      });
  }

  private _showErrorSnackbar(message: string) {
    const translatedMessage = this.translate.instant(message);
    this.ts.error(translatedMessage);
  }
}
